import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Circle, Calendar, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface LessonCardProps {
  lessonNumber: number;
  dayOfWeek: string;
  scheduledDate: string;
  isCompleted: boolean;
  exerciseCount: number;
  onClick: () => void;
}

export default function LessonCard({
  lessonNumber,
  dayOfWeek,
  scheduledDate,
  isCompleted,
  exerciseCount,
  onClick,
}: LessonCardProps) {
  const isPast = new Date(scheduledDate) < new Date();
  const isToday =
    format(new Date(scheduledDate), "yyyy-MM-dd") ===
    format(new Date(), "yyyy-MM-dd");

  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2 }}
    >
      <Card
        className={cn(
          "cursor-pointer transition-all hover:shadow-lg",
          isCompleted && "bg-green-50 dark:bg-green-950/20 border-green-300 dark:border-green-800",
          isToday && !isCompleted && "border-pink-400 dark:border-pink-600 shadow-md",
          !isCompleted && !isToday && "border-border"
        )}
        onClick={onClick}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              {isCompleted ? (
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
              ) : (
                <Circle className="h-5 w-5 text-muted-foreground" />
              )}
              <div>
                <h3 className="font-semibold text-sm">Aula {lessonNumber}</h3>
                <p className="text-xs text-muted-foreground">{dayOfWeek}</p>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Calendar className="h-3.5 w-3.5" />
            <span>{format(new Date(scheduledDate), "dd/MM/yyyy", { locale: ptBR })}</span>
          </div>

          <div className="flex items-center justify-between">
            <Badge
              variant="secondary"
              className="text-xs bg-pink-100 dark:bg-pink-900/50 text-pink-700 dark:text-pink-300"
            >
              {exerciseCount} exercícios
            </Badge>
            {isToday && !isCompleted && (
              <Badge className="text-xs bg-pink-500 text-white">Hoje</Badge>
            )}
            {isCompleted && (
              <Badge className="text-xs bg-green-600 text-white">Realizada</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
